import React, { useMemo, useState } from "react";
import { AxiosError } from "axios";
import { Document, Page, pdfjs } from "react-pdf";
import { ContractDocumentModel } from "../../models/ContrattoModel";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  Tooltip,
  Alert,
  Snackbar,
} from "@mui/material";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { WithToggleAndReadonlyAndData } from "../../models/shared/CommonModels";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { LoadingButton } from "@mui/lab";
import EmailIcon from "@mui/icons-material/Email";
import { resendContractEmail } from "../../api/contract/document";

interface ContractEmailErrorResponse {
  message?: string;
  error_code?: string;
  provider_code?: string | number | null;
}

interface ContractDocumentPreviewProps
  extends WithToggleAndReadonlyAndData<ContractDocumentModel> {
  contractId?: number;
}

const ContractDocumentPreview: React.FC<ContractDocumentPreviewProps> = ({
  initialData,
  open,
  toggleOpen,
  readonly,
  handleSubmit,
  handleClose,
  contractId,
}) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);

  const [completedLoading, setCompletedLoading] = useState<boolean>(false);
  const [loadingDownload, setLoadingDownload] = useState<boolean>(false);
  const [loadingEmail, setLoadingEmail] = useState<boolean>(false);
  const [notification, setNotification] = useState<{
    severity: "success" | "error";
    message: string;
  } | null>(null);

  // Use CDN worker to avoid bundling issues
  pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

  const goToPrevPage = () =>
    setPageNumber(pageNumber - 1 <= 1 ? 1 : pageNumber - 1);

  const goToNextPage = () =>
    setPageNumber(pageNumber + 1 >= numPages ? numPages : pageNumber + 1);

  const pdfDocument = useMemo(
    () => `data:application/pdf;base64,${initialData.base64ContractPdf}`,
    [initialData.base64ContractPdf]
  );

  const handleClosing = () => {
    setPageNumber(1);
    if (handleClose !== undefined) handleClose();
    toggleOpen();
  };

  const downloadBase64Pdf = (base64Data: string, fileName: string) => {
    const link = document.createElement("a");
    link.href = `data:application/pdf;base64,${base64Data}`;
    link.download = fileName || "download.pdf";
    link.click();
  };

  const handleResendEmail = async () => {
    if (contractId === undefined) return;
    if (!window.confirm("Inviare nuovamente il contratto all'email preferita del cliente?")) return;

    setLoadingEmail(true);
    try {
      const response = await resendContractEmail(contractId);
      const recipient = response.data?.recipient;
      setNotification({
        severity: "success",
        message: recipient
          ? `Email inviata con successo all'indirizzo ${recipient}`
          : response.data?.message || "Email inviata con successo",
      });
    } catch (error) {
      const axiosError = error as AxiosError<ContractEmailErrorResponse>;
      const errorData = axiosError.response?.data;
      const errorCode = errorData?.error_code || axiosError.response?.status || "UNKNOWN";
      const providerCode = errorData?.provider_code
        ? `, provider: ${errorData.provider_code}`
        : "";
      setNotification({
        severity: "error",
        message: `${errorData?.message || "Invio email non riuscito"} (codice: ${errorCode}${providerCode})`,
      });
    } finally {
      setLoadingEmail(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        PaperProps={{
          component: "form",
          onSubmit: (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
          },
        }}
        maxWidth="md"
      >
        <DialogTitle>
          Anteprima Contratto
          {numPages > 1 && (
            <>
              : {pageNumber} / {numPages}
            </>
          )}
        </DialogTitle>
        <DialogContent>
          <Document
            file={pdfDocument}
            onLoadSuccess={(pdfInfo) => {
              setNumPages(pdfInfo.numPages);
              setCompletedLoading(true);
            }}
            loading={
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <CircularProgress />
              </Box>
            }
            error={
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <CircularProgress />
              </Box>
            }
          >
            <Page
              pageNumber={pageNumber}
              className={"contractDocumentPage"}
              renderAnnotationLayer={false}
              renderTextLayer={true}
              // loading={
              //   <Box sx={{ display: "flex" }}>
              //     <CircularProgress />
              //   </Box>
              // }
            />
          </Document>
        </DialogContent>
        <DialogActions
          sx={{
            paddingLeft: "18pt",
            paddingRight: "18pt",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ justifyContent: "flex-start" }}>
            {completedLoading && (
              <Button
                onClick={() => {
                  toggleOpen();
                  if (handleSubmit !== undefined) handleSubmit();
                }}
              >
                Chiudi
              </Button>
            )}
          </Box>
          <Box sx={{ justifyContent: "flex-end" }}>
            {numPages > 1 && (
              <>
                {pageNumber !== 1 ? (
                  <Button
                    sx={{ marginLeft: "5pt" }}
                    onClick={() => goToPrevPage()}
                    variant="contained"
                    color="secondary"
                  >
                    {"<"}
                  </Button>
                ) : (
                  <></>
                )}
                {pageNumber < numPages ? (
                  <Button
                    sx={{ marginLeft: "5pt" }}
                    onClick={() => goToNextPage()}
                    variant="contained"
                    color="secondary"
                  >
                    {">"}
                  </Button>
                ) : (
                  <></>
                )}
              </>
            )}
            {completedLoading ? (
              <>
              {contractId !== undefined && (
                <Tooltip title="Invia nuovamente all'email preferita del cliente">
                  <LoadingButton
                    sx={{ marginLeft: "5pt" }}
                    loading={loadingEmail}
                    color="secondary"
                    variant="outlined"
                    startIcon={<EmailIcon color="secondary" />}
                    onClick={handleResendEmail}
                  >
                    Invia via email
                  </LoadingButton>
                </Tooltip>
              )}
              <Tooltip title="Scarica">
                <LoadingButton
                  sx={{ marginLeft: "5pt" }}
                  disabled={loadingDownload}
                  loading={loadingDownload}
                  color="secondary"
                  variant="outlined"
                  className="action_icon"
                  startIcon={<FileDownloadIcon color="secondary" />}
                  onClick={() => {
                    setLoadingDownload(true);
                    downloadBase64Pdf(
                      initialData.base64ContractPdf,
                      "contratto_preview.pdf"
                    );
                    setLoadingDownload(false);
                  }}
                >
                  Salva
                </LoadingButton>
              </Tooltip>
              </>
            ) : (
              <></>
            )}
          </Box>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={notification !== null}
        autoHideDuration={6000}
        onClose={() => setNotification(null)}
      >
        <Alert
          severity={notification?.severity || "success"}
          onClose={() => setNotification(null)}
        >
          {notification?.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ContractDocumentPreview;
